import React, { useState, useEffect } from "react";
import axios from "axios";
import GenericCard from "components/UI/GenericCard/GenericCard";
import ActiveLight from "components/UI/ActiveLight/ActiveLight";

const RenderOpen = ({ children }) => (
  <div className="flex item-center h-full">
    <ActiveLight text="Open" />
    <p className="flex items-center ps-[10px] pt-1">{children}</p>
  </div>
);

const RenderClosed = ({ children }) => (
  <span>
    <span className="red text-[1.25rem] pe-2">Closed </span>
    {children}
  </span>
);

const NasdaqStatus = () => {
  const [nasdaqStatus, setNasdaqStatus] = useState("");
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);

  const returnIsInHoursRange = (date, startHour, endHour) => {
    const currentHour = date.getHours();
    const currentMinute = date.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    return (
      currentTimeInMinutes >= startHour * 60 &&
      currentTimeInMinutes <= endHour * 60
    );
  };

  useEffect(() => {
    const checkNasdaqStatus = async () => {
      const nyseTime = new Date().toLocaleString("en-US", {
        timeZone: "America/New_York",
      });

      const now = new Date(nyseTime);
      const currentDay = now.getDay(); // 0 is Sunday, 1 is Monday, etc.

      // Fetch holiday data for the current year (United States)
      const year = now.getFullYear();
      const { data: holidays } = await axios.get(
        `https://date.nager.at/api/v3/PublicHolidays/${year}/US`,
      );

      // Check if today is a holiday
      const holiday = holidays.find((holiday) => {
        const holidayDate = new Date(holiday.date);
        return (
          holidayDate.getMonth() === now.getMonth() &&
          holidayDate.getDate() === now.getDate()
        );
      });

      if (holiday) {
        setNasdaqStatus(<RenderClosed>Holiday: {holiday.name}</RenderClosed>);
        return;
      }

      // Check for upcoming holidays in the next 7 days
      const nextWeek = new Date(now);
      nextWeek.setDate(nextWeek.getDate() + 7);

      const upcoming = holidays
        .filter((holiday) => {
          const holidayDate = new Date(holiday.date);
          return holidayDate > now && holidayDate <= nextWeek;
        })
        .map((holiday) => ({
          name: holiday.name,
          date: new Date(holiday.date),
        }));

      setUpcomingHolidays(upcoming);

      const isWeekday = currentDay >= 1 && currentDay <= 5; // Monday to Friday
      if (!isWeekday) {
        setNasdaqStatus(<RenderClosed />);
        return;
      }

      const tradingHours = [
        { label: "(Pre-Market)", start: 4, end: 9.5 },
        { label: "(Regular Trading Hours)", start: 9.5, end: 16 },
        { label: "(After-hours)", start: 16, end: 20 },
      ];

      const tradingStatus = tradingHours.find(({ start, end }) =>
        returnIsInHoursRange(now, start, end),
      );

      if (tradingStatus) {
        setNasdaqStatus(<RenderOpen>{tradingStatus.label}</RenderOpen>);
      } else {
        setNasdaqStatus(<RenderClosed />);
      }
    };

    checkNasdaqStatus();

    // Refresh every minute
    const interval = setInterval(checkNasdaqStatus, 60000);

    return () => clearInterval(interval);
  }, []);

  return (
    <GenericCard
      minHeight={85}
      className="flex-grow"
      title="Market Status"
      disableToggle
    >
      <div>
        <p>{nasdaqStatus}</p>
        {upcomingHolidays.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-sm font-semibold mb-2">Upcoming Holidays:</p>
            <ul className="text-sm space-y-1">
              {upcomingHolidays.map((holiday, index) => (
                <li key={index} className="text-gray-600">
                  {holiday.name} -{" "}
                  {holiday.date.toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </GenericCard>
  );
};

export default NasdaqStatus;
